import React from "react";
import { TrendingUp, Users, Clock, Smartphone, Monitor } from "lucide-react";

interface UserTrackingAnalyticsProps {
  analytics: any;
  isLoading: boolean;
  error?: string | null;
  days?: number;
}

const UserTrackingAnalytics: React.FC<UserTrackingAnalyticsProps> = ({
  analytics,
  isLoading,
  error,
  days,
}) => {
  const statsCards = [
    {
      title: "Total Users",
      value: analytics?.totalUsers || 0,
      icon: Users,
      color: "blue",
      change: 0,
    },
    {
      title: "Active Users",
      value: analytics?.activeToday || 0,
      icon: TrendingUp,
      color: "green",
      change: 0,
    },
    {
      title: "New Registrations",
      value: analytics?.registeredInPeriod || 0,
      icon: Clock,
      color: "purple",
      change: 0,
    },
    {
      title: "Mobile Users",
      value: analytics?.mobileUsers || 0,
      icon: Smartphone,
      color: "orange",
      change: 0,
    },
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      blue: {
        bg: "bg-blue-50",
        text: "text-blue-600",
        iconBg: "bg-blue-100",
        iconText: "text-blue-600",
      },
      green: {
        bg: "bg-green-50",
        text: "text-green-600",
        iconBg: "bg-green-100",
        iconText: "text-green-600",
      },
      purple: {
        bg: "bg-purple-50",
        text: "text-purple-600",
        iconBg: "bg-purple-100",
        iconText: "text-purple-600",
      },
      orange: {
        bg: "bg-orange-50",
        text: "text-orange-600",
        iconBg: "bg-orange-100",
        iconText: "text-orange-600",
      },
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="bg-gray-100 rounded-lg p-6 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-16"></div>
              </div>
              <div className="h-12 w-12 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Summary Information */}
      {analytics?.summary && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-blue-800">
                Analytics Summary for {analytics.summary.periodDescription}
              </h3>
              <p className="text-sm text-blue-600 mt-1">
                <strong>Activity:</strong> {analytics.summary.activeInPeriod}{" "}
                out of {analytics.summary.totalRegistered} total users were
                active ({analytics.summary.activePercentage}% activity rate)
                <br />
                <strong>Registrations:</strong>{" "}
                {analytics.summary.registeredInPeriod} new users registered (
                {analytics.summary.registrationPercentage}% of total users)
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-800">
                {analytics.summary.activePercentage}%
              </div>
              <div className="text-xs text-blue-600">Activity Rate</div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, index) => {
          const IconComponent = stat.icon;
          const colors = getColorClasses(stat.color);

          return (
            <div
              key={index}
              className={`${colors.bg} rounded-lg p-6 border border-gray-200`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className={`text-sm font-medium ${colors.text}`}>
                    {stat.title}
                  </p>
                  <p className={`text-2xl font-bold ${colors.text}`}>
                    {stat.value.toLocaleString()}
                  </p>
                </div>
                <div className={`${colors.iconBg} p-3 rounded-lg`}>
                  <IconComponent className={`h-6 w-6 ${colors.iconText}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Daily Activity Chart */}
      {analytics?.dailyLogins && analytics.dailyLogins.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Daily Activity (Last {days} days)
          </h3>
          <div className="space-y-3">
            {analytics.dailyLogins
              .slice(0, 7)
              .map((day: any, index: number) => (
                <div
                  key={day.date}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-900">
                      {new Date(day.date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">
                      {day.uniqueUsers} users
                    </span>
                    <span className="text-xs text-gray-500">
                      {day.totalLogins} logins
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Platform & Version Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Platform Breakdown */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Platform Breakdown
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 p-2 rounded-lg">
                  <Smartphone className="h-4 w-4 text-green-600" />
                </div>
                <span className="text-sm font-medium text-gray-900">
                  Android
                </span>
              </div>
              <span className="text-sm text-gray-600">
                {analytics?.platformDistribution?.find(
                  (p: any) => p.platform === "android"
                )?.userCount || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Smartphone className="h-4 w-4 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-gray-900">iOS</span>
              </div>
              <span className="text-sm text-gray-600">
                {analytics?.platformDistribution?.find(
                  (p: any) => p.platform === "ios"
                )?.userCount || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <Monitor className="h-4 w-4 text-purple-600" />
                </div>
                <span className="text-sm font-medium text-gray-900">Web</span>
              </div>
              <span className="text-sm text-gray-600">
                {analytics?.platformDistribution?.find(
                  (p: any) => p.platform === "web"
                )?.userCount || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-orange-100 p-2 rounded-lg">
                  <Monitor className="h-4 w-4 text-orange-600" />
                </div>
                <span className="text-sm font-medium text-gray-900">
                  Tablet
                </span>
              </div>
              <span className="text-sm text-gray-600">
                {analytics?.platformDistribution?.find(
                  (p: any) => p.platform === "tablet"
                )?.userCount || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Version Breakdown */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Version Breakdown
          </h3>
          <div className="space-y-3">
            {analytics?.versionDistribution &&
            analytics.versionDistribution.length > 0 ? (
              analytics.versionDistribution.map((version: any) => (
                <div
                  key={`${version.version}-${version.platform}`}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      v{version.version}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({version.platform || "unknown"})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">
                      {version.userCount}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({version.percentage?.toFixed(1)}%)
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No version data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserTrackingAnalytics;
